/**
 * ActivitiesListController — onglet « Activités » de /profil-entreprise.html
 * (EPIC-006 / 6.4).
 *
 * Responsabilités :
 *   - Charger la liste des activités au `connect()` via GET /activities.
 *   - Hydrater les cards via <template id="tpl-activity-card"> + cloneTemplate
 *     + fillSlots (jamais d'HTML inline en JS — règle CLAUDE.md).
 *   - Gérer le clic « Définir comme principale » (confirm + POST set-principal).
 *   - Gérer le clic « Archiver » :
 *     - secondaire → confirm + POST archive
 *     - principale → modale de re-désignation forcée + POST archive
 *       avec replacementPrincipalId.
 *   - Cacher le bouton « Définir comme principale » sur la card déjà principale.
 *   - Afficher un bandeau « Modification non signée à l'INPI » par card si
 *     `pendingFormality` est présente (V1 : statique informatif, EPIC-008
 *     ajoutera le CTA actif vers procedures.inpi.fr).
 *
 * Le controller `activity-form` (modale d'ajout/édition) est distinct ; il
 * dispatche un événement custom `activity-form:saved` que ce controller
 * écoute pour rafraîchir la liste.
 *
 * Convention CLAUDE.md respectée :
 *   - 0 HTML inline en JS (que `cloneTemplate` + `fillSlots` + `textContent`
 *     + `card.classList.add/toggle`).
 *   - Aucune classe CSS inventée — toutes ⊆ notamicro.css.
 *   - Vocabulaire UI : « code APE » plutôt que « NAF » dans les messages.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const CATEGORY_LABELS = {
  bic_vente: "BIC vente",
  bic_services: "BIC services",
  bnc: "BNC",
};

const CATEGORY_PILL_CLASS = {
  bic_vente: "nm-pill--indigo",
  bic_services: "nm-pill--blue",
  bnc: "nm-pill--olive",
};

const REGULATED_LABELS = {
  droit: "Droit",
  sante: "Santé",
  batiment: "Bâtiment",
  immobilier: "Immobilier",
  taxi: "Taxi/VTC",
  autre: "Autre",
};

const ERROR_MESSAGES = {
  invalid_naf_format: "Le code APE est invalide. Format attendu : NN.NNL (ex. 62.01Z).",
  validation_failed: "Certains champs sont invalides. Vérifiez votre saisie.",
  replacement_principal_required: "Vous devez désigner une autre activité comme principale avant d'archiver celle-ci.",
  replacement_principal_invalid: "L'activité de remplacement choisie n'est pas valide.",
  activity_already_principal: "Cette activité est déjà votre activité principale.",
  activity_archived: "Cette activité est archivée et ne peut plus être modifiée.",
  activity_not_found: "Cette activité n'existe pas ou a été supprimée.",
  invalid_request: "Requête invalide.",
  unauthorized: "Vous n'êtes pas autorisé à effectuer cette action.",
};

export default class ActivitiesListController extends BaseController {
  static targets = [
    "cards",
    "emptyState",
    "archiveModal",
    "archiveTargetLabel",
    "archiveReplacementSelect",
    "archiveError",
  ];

  // Cache local — les données complètes (pour pré-remplissage modale d'édition,
  // résolution des descriptions sur archivage, etc.).
  _activities = [];
  _onActivitySaved = null;
  _archivingId = null;

  async connect() {
    // Écoute l'événement émis par activity-form après création/édition réussie.
    this._onActivitySaved = () => this.refresh();
    this.element.addEventListener("activity-form:saved", this._onActivitySaved);
    await this.refresh();
  }

  disconnect() {
    if (this._onActivitySaved) {
      this.element.removeEventListener("activity-form:saved", this._onActivitySaved);
    }
  }

  // ---------------------------------------------------------------------------
  // Fetch + render
  // ---------------------------------------------------------------------------

  async refresh() {
    try {
      const data = await api.listActivities();
      this._activities = Array.isArray(data?.activities) ? data.activities : [];
      // Expose le cache aux autres controllers (ex. activity-form pour pré-
      // remplissage de l'édition). Préfixe `__` pour signaler usage interne
      // inter-controllers — KISS, évite un store Redux/event bus en V1.
      this.element.__activities = this._activities;
      this.render();
      // §99.35 — notifie les controllers qui dépendent du périmètre
      // d'activités actives (ex. profile-entreprise-identite, qui ré-évalue
      // l'éligibilité RCS/RM).
      document.dispatchEvent(new CustomEvent("activities:updated"));
    } catch (err) {
      console.error("Failed to load activities", err);
      this.showError(this._extractMessage(err) || "Impossible de charger les activités.");
    }
  }

  render() {
    // Vider le container (textContent = '' est autorisé — pas de HTML inline).
    this.cardsTarget.textContent = "";

    if (this._activities.length === 0) {
      if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = false;
      return;
    }
    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = true;

    this._activities.forEach((a) => this.cardsTarget.appendChild(this._buildCard(a)));

    // Expose la liste complète aux modales de remplacement principal (le
    // <select> filtre dynamiquement les secondaires actives au moment de
    // l'ouverture).
  }

  _buildCard(activity) {
    const fragment = this.cloneTemplate("tpl-activity-card");
    const article = fragment.querySelector("article");
    if (!article) return fragment;

    article.dataset.activityId = activity.id;
    if (activity.status === "archived") {
      article.dataset.activityArchived = "true";
    }

    // Pills (rôle, catégorie, réglementée, archivée).
    const rolePill = article.querySelector('[data-slot="role-badge"]');
    if (rolePill) {
      rolePill.textContent = activity.role === "principal" ? "Principale" : "Secondaire";
      rolePill.classList.remove("nm-pill--principal");
      if (activity.role === "principal") rolePill.classList.add("nm-pill--principal");
    }

    const categoryPill = article.querySelector('[data-slot="category-badge"]');
    if (categoryPill) {
      categoryPill.textContent = CATEGORY_LABELS[activity.category] || activity.category;
      // Reset puis ajout du modifier de couleur.
      ["nm-pill--blue", "nm-pill--indigo", "nm-pill--olive"].forEach((c) => categoryPill.classList.remove(c));
      const cls = CATEGORY_PILL_CLASS[activity.category];
      if (cls) categoryPill.classList.add(cls);
    }

    const regulatedPill = article.querySelector('[data-slot="regulated-badge"]');
    if (regulatedPill) {
      if (activity.regulatedCategory) {
        regulatedPill.textContent = "Réglementée — " + (REGULATED_LABELS[activity.regulatedCategory] || activity.regulatedCategory);
        regulatedPill.hidden = false;
      } else {
        regulatedPill.hidden = true;
      }
    }

    const archivedPill = article.querySelector('[data-slot="archived-badge"]');
    if (archivedPill) {
      archivedPill.hidden = activity.status !== "archived";
    }

    // Bandeau « Modification non signée à l'INPI » + CTA Signer si
    // pendingFormality LOCAL-* présente (EPIC-008 / 8.7). Le CTA mène à
    // /inpi-redirection-signature.html?formalityId={id} qui pousse la formalité
    // au GU INPI puis redirige vers procedures.inpi.fr pour signature.
    const localFormalityBanner = article.querySelector('[data-slot="local-formality-banner"]');
    if (localFormalityBanner) {
      const hasOpenFormality = !!activity.pendingFormality;
      localFormalityBanner.hidden = !hasOpenFormality || activity.status === "archived";
      const signCta = localFormalityBanner.querySelector('[data-slot="sign-cta"]');
      if (signCta) {
        const formalityId = activity.pendingFormality?.id;
        if (formalityId) {
          signCta.href = `/inpi-redirection-signature.html?formalityId=${encodeURIComponent(formalityId)}`;
          signCta.hidden = false;
        } else {
          signCta.hidden = true;
        }
      }
    }

    // Description, code APE, dates.
    this.fillSlots(article, {
      description: activity.description || "—",
      "naf-code": activity.nafCode || "—",
      "start-date": this._formatDate(activity.startDate),
      location: activity.location || "Établissement principal",
    });

    // Boutons d'action : associer l'id à chaque bouton + masquer selon le rôle.
    const editBtn = article.querySelector("[data-activity-edit]");
    const setPrincipalBtn = article.querySelector("[data-activity-set-principal]");
    const archiveBtn = article.querySelector("[data-activity-archive]");
    const actionsContainer = article.querySelector('[data-slot="actions"]');
    const archivedMeta = article.querySelector('[data-slot="archived-meta"]');
    const archivedOn = article.querySelector('[data-slot="archived-on"]');

    [editBtn, setPrincipalBtn, archiveBtn].forEach((btn) => {
      if (btn) btn.dataset.id = activity.id;
    });
    // EPIC-054 / 54.6 — le bouton Modifier est désormais une ancre vers la
    // page dédiée `/activite-edition.html?id=…` (convention « pas de modal
    // pour create/update », cf. `feedback_no_modal_dedicated_edit_page`).
    if (editBtn && editBtn.tagName === "A") {
      editBtn.href = "/activite-edition.html?id=" + encodeURIComponent(activity.id);
    }

    if (activity.status === "archived") {
      if (actionsContainer) actionsContainer.hidden = true;
      if (archivedMeta) archivedMeta.hidden = false;
      if (archivedOn && activity.archivedAt) {
        archivedOn.textContent = "Archivée le " + this._formatDate(activity.archivedAt);
      }
    } else {
      if (actionsContainer) actionsContainer.hidden = false;
      if (archivedMeta) archivedMeta.hidden = true;
      // L'activité principale ne peut pas être « définie comme principale » à
      // nouveau — on cache simplement le bouton.
      if (setPrincipalBtn && activity.role === "principal") {
        setPrincipalBtn.hidden = true;
      }
    }

    return fragment;
  }

  // ---------------------------------------------------------------------------
  // Action : définir comme principale
  // ---------------------------------------------------------------------------

  async setPrincipalClick(event) {
    const btn = event.currentTarget;
    const id = btn?.dataset?.id;
    if (!id) return;

    const activity = this._findById(id);
    const desc = activity?.description || "cette activité";
    const ok = await this.confirm(
      `Désigner « ${desc} » comme activité principale ?\n\n`
      + "Votre activité actuellement principale deviendra secondaire. "
      + "Cette opération change votre code APE déclaré à l'INSEE et déclenche "
      + "une formalité INPI de modification (à signer ultérieurement sur procedures.inpi.fr).",
      { title: "Changer d'activité principale", confirmLabel: "Confirmer", cancelLabel: "Annuler" },
    );
    if (!ok) return;

    try {
      await api.setPrincipalActivity(id);
      this.showSuccess("Activité principale modifiée. Une formalité INPI a été préparée.");
      await this.refresh();
    } catch (err) {
      this.showError(this._extractMessage(err) || "Impossible de modifier l'activité principale.");
    }
  }

  // ---------------------------------------------------------------------------
  // Action : archiver
  // ---------------------------------------------------------------------------

  async archiveClick(event) {
    const btn = event.currentTarget;
    const id = btn?.dataset?.id;
    if (!id) return;

    const activity = this._findById(id);
    if (!activity) return;

    if (activity.role === "principal") {
      this._openArchiveModal(activity);
      return;
    }

    const desc = activity.description || "cette activité";
    const ok = await this.confirm(
      `Archiver « ${desc} » ?\n\n`
      + "Cette opération déclenche une formalité INPI de modification "
      + "(à signer ultérieurement sur procedures.inpi.fr). "
      + "L'activité reste consultable, marquée comme archivée.",
      { title: "Archiver l'activité", confirmLabel: "Archiver", cancelLabel: "Annuler" },
    );
    if (!ok) return;

    try {
      await api.archiveActivity(id);
      this.showSuccess("Activité archivée. Une formalité INPI a été préparée.");
      await this.refresh();
    } catch (err) {
      this.showError(this._extractMessage(err) || "Impossible d'archiver l'activité.");
    }
  }

  // ---------------------------------------------------------------------------
  // Modale d'archivage avec re-désignation principale (cas activité principale)
  // ---------------------------------------------------------------------------

  _openArchiveModal(activity) {
    if (!this.hasArchiveModalTarget) return;
    this._archivingId = activity.id;

    // Étiquette d'info.
    if (this.hasArchiveTargetLabelTarget) {
      this.archiveTargetLabelTarget.textContent =
        "Activité à archiver : " + (activity.description || "—") + " (code APE " + (activity.nafCode || "—") + ").";
    }

    // Reset erreur.
    if (this.hasArchiveErrorTarget) {
      this.archiveErrorTarget.textContent = "";
      this.archiveErrorTarget.hidden = true;
    }

    // Peupler la liste des activités secondaires actives éligibles (≠ activity).
    if (this.hasArchiveReplacementSelectTarget) {
      const select = this.archiveReplacementSelectTarget;
      // Clear options sauf le placeholder.
      while (select.options.length > 1) select.remove(1);

      const candidates = this._activities.filter(
        (a) => a.id !== activity.id && a.status === "active" && a.role === "secondary",
      );
      candidates.forEach((a) => {
        const opt = document.createElement("option");
        opt.value = a.id;
        opt.textContent = (CATEGORY_LABELS[a.category] || a.category) + " · " + (a.nafCode || "—") + " — " + (a.description || "");
        select.appendChild(opt);
      });
      select.value = "";

      if (candidates.length === 0) {
        // Aucune activité éligible : afficher message d'erreur dans la modale.
        if (this.hasArchiveErrorTarget) {
          this.archiveErrorTarget.textContent =
            "Vous n'avez aucune autre activité active. Ajoutez d'abord une activité secondaire pour pouvoir archiver la principale.";
          this.archiveErrorTarget.hidden = false;
        }
      }
    }

    if (typeof this.archiveModalTarget.showModal === "function") {
      this.archiveModalTarget.showModal();
    } else {
      this.archiveModalTarget.setAttribute("open", "");
    }
  }

  archiveClose() {
    this._archivingId = null;
    if (this.hasArchiveModalTarget) {
      if (typeof this.archiveModalTarget.close === "function") {
        this.archiveModalTarget.close();
      } else {
        this.archiveModalTarget.removeAttribute("open");
      }
    }
  }

  async archiveConfirm() {
    if (!this._archivingId) return;
    const replacementId = this.hasArchiveReplacementSelectTarget
      ? this.archiveReplacementSelectTarget.value
      : "";

    if (!replacementId) {
      if (this.hasArchiveErrorTarget) {
        this.archiveErrorTarget.textContent = "Sélectionnez l'activité qui deviendra principale.";
        this.archiveErrorTarget.hidden = false;
      }
      return;
    }

    try {
      await api.archiveActivity(this._archivingId, replacementId);
      this.archiveClose();
      this.showSuccess("Activité archivée et nouvelle activité principale désignée.");
      await this.refresh();
    } catch (err) {
      const msg = this._extractMessage(err) || "Impossible d'archiver l'activité.";
      if (this.hasArchiveErrorTarget) {
        this.archiveErrorTarget.textContent = msg;
        this.archiveErrorTarget.hidden = false;
      } else {
        this.showError(msg);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers internes
  // ---------------------------------------------------------------------------

  _findById(id) {
    return this._activities.find((a) => a.id === id) || null;
  }

  _formatDate(iso) {
    if (!iso) return "—";
    try {
      // Le backend renvoie soit "YYYY-MM-DD" soit ISO 8601 complet.
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    } catch (_) {
      return "—";
    }
  }

  _extractMessage(err) {
    // APIError carries .data: { error: { code, message } }
    const code = err?.data?.error?.code || err?.error?.code;
    if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
    if (err?.data?.error?.message) return err.data.error.message;
    if (err?.message) return err.message;
    return null;
  }
}
